import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { signOut } from 'firebase/auth';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  increment
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../context/AuthContext';
import './DonorDashboard.css';
import { trackEvent } from "../../utils/analytics";
import ImpactMap from "../../components/ImpactMap";


const PAYMENT_METHOD_ROUTE = '/give/payment-method';

function NetworkAttributionBadge({ networkName }) {
  if (!networkName) return null;

  return (
    <div className="network-attribution-badge">
      Impact Network: <strong>{networkName}</strong>
    </div>
  );
}



function formatDate(value) {
  if (!value) return null;
  const d = value?.toDate ? value.toDate() : new Date(value);
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
}

function formatCurrency(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}

function formatSubscriptionDates(sub) {
  const parts = [];
  if (sub.lastBilledDate) parts.push(`Last billed ${formatDate(sub.lastBilledDate)}`);
  if (sub.orderStatus === 'needed' && sub.orderDueDate) parts.push(`Order due ${formatDate(sub.orderDueDate)}`);
  if ((sub.orderStatus === 'ordered' || sub.latestOrder?.status === 'ordered') && sub.latestOrder?.targetDeliveryDate) {
    parts.push(`Delivery by ${formatDate(sub.latestOrder.targetDeliveryDate)}`);
  }
  if ((sub.orderStatus === 'delivered' || sub.latestOrder?.status === 'delivered') && sub.latestOrder?.deliveredAt) {
    parts.push(`Delivered ${formatDate(sub.latestOrder.deliveredAt)}`);
  }
  if (sub.nextBillingDate && sub.status === 'active') parts.push(`Next billing ${formatDate(sub.nextBillingDate)}`);
  return parts.length ? parts.join(' · ') : null;
}

function getMilestone(sub) {
  const d = sub.totalDeliveries || 0;
  if (d >= 12) return '1 year of support';
  if (d >= 6) return '6 months of support';
  if (d >= 3) return '3 months of support';
  return null;
}

function getFulfillmentStatus(sub) {
  if (sub.orderStatus === 'delivered' || sub.latestOrder?.status === 'delivered') return { label: 'Delivered', color: '#065f46', bg: '#d1fae5' };
  if (sub.orderStatus === 'shipped'   || sub.latestOrder?.status === 'shipped')   return { label: 'Shipped',   color: '#1e40af', bg: '#dbeafe' };
  if (sub.orderStatus === 'ordered'   || sub.latestOrder?.status === 'ordered')   return { label: 'Ordered',   color: '#1e40af', bg: '#dbeafe' };
  if (sub.orderStatus === 'needed') return { label: 'Awaiting order', color: '#92400e', bg: '#fef3c7' };
  return null;
}





// ─── Main Dashboard ───────────────────────────────────────────────────────────

function buildImpactSummary(subscriptions) {
  const lifetime = subscriptions.filter(s =>
    ['active', 'completed', 'canceled', 'cancelled', 'pending_replacement'].includes(s.status)
  );

  const byUnit = {};
  lifetime.forEach(s => {
    const unit = (s.impactUnit || 'impact').trim();
    const amount = Number(s.totalImpact || 0);
    if (!amount) return;
    byUnit[unit] = (byUnit[unit] || 0) + amount;
  });

  const allImpacts = Object.entries(byUnit)
  .sort((a, b) => b[1] - a[1])
  .map(([unit, amount]) => ({ unit, amount }));

  const topImpacts = allImpacts.slice(0, 4);
  const additionalImpactCount = Math.max(0, allImpacts.length - topImpacts.length);

  
  const active = subscriptions.filter(s => s.status === 'active');
  const orgCount = new Set(active.map(s => s.orgId || s.organizationId || s.orgName).filter(Boolean)).size;
  const monthlyTotal = active.reduce((n, s) => n + Number(s.pricePerCycle || 0), 0);
  const deliveriesMade = lifetime.reduce((n, s) => n + Number(s.totalDeliveries || 0), 0);

  return {
  activeCommitments: active.length,
  orgCount,
  monthlyTotal,
  deliveriesMade,
  topImpacts,
  additionalImpactCount,
};
}

function formatImpactLabel(unit) {
  return unit
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}


function DonorDashboard() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  const [subscriptions, setSubscriptions]     = useState([]);
  const [payments, setPayments]               = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [dashboardTab, setDashboardTab]       = useState('active');
  const [replacementBusy, setReplacementBusy] = useState({});
  

  useEffect(() => { if (auth.currentUser) fetchDashboardData(); }, []);

  async function fetchDashboardData() {
    try {
      setLoading(true);
      const [subsSnap, ordersSnap, paymentsSnap] = await Promise.all([
        getDocs(query(collection(db, 'subscriptions'), where('donorId', '==', auth.currentUser.uid))),
        getDocs(query(collection(db, 'orders'),        where('donorId', '==', auth.currentUser.uid))),
        getDocs(query(collection(db, 'payments'),      where('donorId', '==', auth.currentUser.uid))),
      ]);

      const allSubsRaw = subsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const orgIds = [...new Set(allSubsRaw.map(s => s.orgId || s.organizationId).filter(Boolean))];
      const orgLogoById = {};
      await Promise.all(orgIds.map(async (orgId) => {
        try {
          const snap = await getDoc(doc(db, 'organizations', orgId));
          if (snap.exists()) {
            const o = snap.data();
            orgLogoById[orgId] = o.logoUrl || o.logoURL || o.logo || o.orgLogo || o.imageUrl || '';
          }
        } catch (e) { console.warn('org logo fail', orgId, e); }
      }));

      const allSubs = allSubsRaw.map(sub => ({
        ...sub,
        orgLogo: sub.orgLogo || sub.logoUrl || sub.organizationLogo || orgLogoById[sub.orgId || sub.organizationId] || ''
      }));

      const allPayments = paymentsSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.billedAt?.toDate?.()?.getTime?.() || 0) - (a.billedAt?.toDate?.()?.getTime?.() || 0));

      const latestOrder = {};
      ordersSnap.docs.forEach(d => {
        const o = { id: d.id, ...d.data() };
        if (!o.subscriptionId) return;
        const getTs = v => v?.toDate ? v.toDate().getTime() : v ? new Date(v).getTime() : 0;
        const t  = getTs(o.deliveredAt) || getTs(o.orderedAt) || getTs(o.targetDeliveryDate) || getTs(o.billedDate);
        const ex = latestOrder[o.subscriptionId];
        const exV = ex && (ex.deliveredAt || ex.orderedAt || ex.targetDeliveryDate || ex.billedDate);
        const exT = exV?.toDate ? exV.toDate().getTime() : exV ? new Date(exV).getTime() : 0;
        if (!ex || t > exT) latestOrder[o.subscriptionId] = o;
      });

      const enriched = allSubs.map(s => ({ ...s, latestOrder: latestOrder[s.id] || null }));
      

      setSubscriptions(enriched);
      setPayments(allPayments);
      
    } catch (err) {
      console.error('fetchDashboardData error', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleCancel(sub) {
    if (!window.confirm(
      'End this commitment?\n\nThis will stop future billing for this need. ' +
      'If this month\'s shipment is already in process, it will still be completed before the need returns to the open list.\n\n' +
      'Your past deliveries and impact will remain in your account history.'
    )) return;
    trackEvent('cancel_commitment', { need_id: sub.needId, org_name: sub.orgName });
    try {
      const inFlight = sub.orderStatus === 'needed' || sub.orderStatus === 'ordered';
      const upd = { status: 'canceled', canceledAt: new Date(), nextBillingDate: null };
      if (inFlight) { upd.pendingCancellation = true; upd.reopenNeedAfterFulfillment = true; }
      await updateDoc(doc(db, 'subscriptions', sub.id), upd);
      await updateDoc(doc(db, 'needs', sub.needId), {
        sponsorCount: increment(-1),
        ...(inFlight ? {} : { status: 'open' })
      });
      await fetchDashboardData();
    } catch (err) {
      console.error('cancel error', err);
      alert('Something went wrong. Please try again.');
    }
  }

  async function handleApproveReplacement(sub) {
    if (!window.confirm(
      'Approve this replacement?\n\nYour old commitment will be ended and a new commitment will be created for the replacement need.'
    )) return;
    try {
      setReplacementBusy(p => ({ ...p, [sub.id]: true }));
      const fn = httpsCallable(getFunctions(), 'approveReplacement');
      await fn({ subscriptionId: sub.id, replacementNeedId: sub.replacementNeedId });
      await fetchDashboardData();
      alert('Replacement approved.');
    } catch (err) {
      console.error('approve replacement error', err);
      alert('Error approving replacement: ' + err.message);
    } finally {
      setReplacementBusy(p => ({ ...p, [sub.id]: false }));
    }
  }

  async function handleDeclineReplacement(sub) {
    if (!window.confirm(
      'Decline this replacement?\n\nYour current commitment will be canceled and you will not be moved to the replacement need.'
    )) return;
    await handleCancel(sub);
  }

  async function handleSignOut() { await signOut(auth); navigate('/'); }

  const activeSubscriptions = useMemo(() => subscriptions.filter(s => s.status === 'active'), [subscriptions]);
  const pendingReplacements  = useMemo(() => subscriptions.filter(s => s.status === 'pending_replacement'), [subscriptions]);

  const impactSummary = useMemo(
  () => buildImpactSummary(subscriptions),
  [subscriptions]
);



  const upcomingBillingGroups = useMemo(() => {
    const sorted = activeSubscriptions
      .filter(s => s.nextBillingDate)
      .sort((a, b) => (a.nextBillingDate?.toDate?.()?.getTime?.() || 0) - (b.nextBillingDate?.toDate?.()?.getTime?.() || 0));
    const g = {};
    sorted.forEach(s => { const k = formatDate(s.nextBillingDate); if (!g[k]) g[k] = []; g[k].push(s); });
    return Object.entries(g).map(([date, items]) => ({
      date, items, total: items.reduce((n, i) => n + Number(i.pricePerCycle || 0), 0)
    }));
  }, [activeSubscriptions]);

  const currentPaymentMethod = useMemo(() => {
    if (!userProfile?.cardLast4) return null;
    const brand = userProfile.cardBrand
      ? userProfile.cardBrand.charAt(0).toUpperCase() + userProfile.cardBrand.slice(1)
      : 'Card';
    return `${brand} ending in ${userProfile.cardLast4}`;
  }, [userProfile]);

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="logo" onClick={() => navigate('/')}>somebody.gives</div>
        <nav className="dashboard-nav">
  <span
    className="nav-link active"
    onClick={() => navigate('/give/dashboard')}
  >
    My commitments
  </span>

<span
  className="nav-link"
  onClick={() => navigate('/donor/impact-networks')}
>
  Impact Networks
</span>

  <span
  className="nav-link"
  onClick={() => navigate('/give/causes')}
>
  Browse causes
</span>

  <span
    className="nav-link"
    onClick={() => {
      trackEvent('click_browse_needs', { source: 'dashboard' });
      navigate('/give/browse');
    }}
  >
    Browse needs
  </span>

  <span
    className="nav-link"
    onClick={() => {
      trackEvent('click_browse_sponsorships', { source: 'dashboard' });
      navigate('/give/sponsorships');
    }}
  >
    Browse sponsorships
  </span>

  <span
    className="nav-link"
    onClick={() => navigate('/give/orgs')}
  >
    Browse orgs
  </span>
</nav>
        <div className="dashboard-user">
          <span className="user-name">{userProfile?.firstName || 'Donor'}</span>
          <button onClick={handleSignOut} className="btn-signout">Sign out</button>
        </div>
      </header>

      <main className="dashboard-main">
        <div className="dashboard-welcome">
          <h1>Welcome back, {userProfile?.firstName || 'there'}.</h1>
          <p>Here are the needs you are responsible for.</p>

<button
  className="btn-primary"
  style={{ width: 'auto', marginTop: '1rem' }}
  onClick={() => navigate('/donor/impact-networks')}
>
  View Impact Networks →
</button>
        </div>

        <section className="donor-impact-hero">
  <div className="impact-hero-copy">
    <div className="impact-kicker">Your local impact</div>
    <h2>
      You are helping {impactSummary.orgCount} local nonprofit{impactSummary.orgCount === 1 ? '' : 's'}
      {' '}consistently care for people in Springfield.
    </h2>
    <p>
      Your {impactSummary.activeCommitments} recurring commitment{impactSummary.activeCommitments === 1 ? '' : 's'}
      {' '}provide predictable support worth {formatCurrency(impactSummary.monthlyTotal)}/month.
    </p>
  </div>

  <div className="impact-proof-grid">
  {impactSummary.topImpacts.length > 0 ? impactSummary.topImpacts.map(item => (
    <div key={item.unit} className="impact-proof-card">
      <div className="impact-proof-number">{item.amount.toLocaleString()}</div>
      <div className="impact-proof-label">
  {formatImpactLabel(item.unit)}
</div>
    </div>
  )) : (
    <div className="impact-proof-card">
      <div className="impact-proof-number">{impactSummary.deliveriesMade}</div>
      <div className="impact-proof-label">deliveries completed</div>
    </div>
  )}

  {impactSummary.additionalImpactCount > 0 && (
    <div className="impact-more-note">
      Plus support across {impactSummary.additionalImpactCount} additional impact area
      {impactSummary.additionalImpactCount === 1 ? '' : 's'}.
    </div>
  )}
</div>
</section>

        <div style={{ display: 'flex', gap: '0.75rem', borderBottom: '1px solid #eee', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          {[
            { key: 'active',   label: 'Active commitments' },
            { key: 'impact',   label: 'Impact map' },
            { key: 'upcoming', label: 'Upcoming bills' },
            { key: 'payments', label: 'Payments & method' },
          ].map(tab => (
            <button key={tab.key} onClick={() => setDashboardTab(tab.key)} style={{
              background: 'transparent', border: 'none',
              borderBottom: dashboardTab === tab.key ? '2px solid #1a1a1a' : '2px solid transparent',
              color: dashboardTab === tab.key ? '#1a1a1a' : '#666',
              padding: '0.5rem 0.25rem',
              fontWeight: dashboardTab === tab.key ? 700 : 500,
              cursor: 'pointer', fontSize: '0.95rem',
            }}>{tab.label}</button>
          ))}
        </div>

        {loading && <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>Loading your dashboard...</div>}

        {/* Active tab */}
        {!loading && dashboardTab === 'active' && pendingReplacements.length > 0 && (
          <div className="subscriptions-list" style={{ marginBottom: '1.5rem' }}>
            <div className="section-header"><h2>Action needed</h2></div>
            {pendingReplacements.map(sub => (
              <div key={sub.id} className="subscription-card" style={{ border: '1px solid #f59e0b', background: '#fffbeb' }}>
                <div className="sub-left">
                  <div className="sub-badges">
                    <span style={{ background: '#fef3c7', color: '#92400e', padding: '0.2rem 0.55rem', borderRadius: '20px', fontSize: '0.72rem', fontWeight: 700 }}>replacement requested</span>
                  </div>
                  <div className="sub-org">{sub.orgName}</div>
                  <div className="sub-item">{sub.itemName}</div>
                  <div className="sub-impact">This need is no longer available. The organization has suggested a replacement.</div>
                  <div style={{ fontSize: '0.85rem', color: '#555', marginTop: '0.65rem' }}>
                    <div>Current item: <strong>{sub.oldNeedName || sub.itemName}</strong> — {formatCurrency(sub.oldPricePerCycle || sub.pricePerCycle)}/{sub.oldFrequency || sub.frequency}</div>
                    <div style={{ marginTop: '0.25rem' }}>Suggested replacement: <strong>{sub.replacementNeedName || 'Replacement need'}</strong> — {formatCurrency(sub.replacementPricePerCycle)}/{sub.replacementFrequency || sub.frequency}</div>
                  </div>
                  <div style={{ fontSize: '0.82rem', color: '#92400e', marginTop: '0.5rem' }}>You will not be moved to a different need unless you approve it.</div>
                </div>
                <div className="sub-right">
                  <button className="btn-primary" style={{ marginBottom: '0.5rem' }} disabled={!!replacementBusy[sub.id]} onClick={() => handleApproveReplacement(sub)}>
                    {replacementBusy[sub.id] ? 'Saving...' : 'Approve replacement'}
                  </button>
                  <button className="btn-cancel-sub" disabled={!!replacementBusy[sub.id]} onClick={() => handleDeclineReplacement(sub)}>Cancel instead</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && dashboardTab === 'active' && activeSubscriptions.length === 0 && pendingReplacements.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <h2>You have no active commitments right now</h2>
            <p>Your past impact is preserved below in payment history. Browse open needs to take responsibility for something specific again.</p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => { trackEvent('click_browse_needs', { source: 'dashboard' }); navigate('/give/browse'); }} className="btn-primary">Browse open needs →</button>
              <button onClick={() => { trackEvent('click_browse_sponsorships', { source: 'dashboard_empty' }); navigate('/give/sponsorships'); }} className="btn-primary">Browse sponsorships →</button>
              <button onClick={() => navigate('/give/orgs')} className="btn-primary">Browse organizations →</button>
              <button onClick={() => navigate('/give/causes')} className="btn-primary">Find a cause →</button>
            </div>
          </div>
        )}

        {!loading && dashboardTab === 'active' && activeSubscriptions.length > 0 && (
          <div className="subscriptions-list">
            <div className="section-header">
              <h2>Your active commitments</h2>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <span className="browse-link" onClick={() => navigate('/give/causes')}>Browse causes →</span>
<span className="browse-link" onClick={() => navigate('/give/browse')}>Browse more needs →</span>
<span className="browse-link" onClick={() => navigate('/give/sponsorships')}>Browse sponsorships →</span>
<span className="browse-link" onClick={() => navigate('/give/orgs')}>Browse orgs →</span>
              </div>
            </div>
            {activeSubscriptions.map(sub => {
              const milestone = getMilestone(sub);
              const fs = getFulfillmentStatus(sub);
              return (
                <div key={sub.id} className="subscription-card">
                  <div className="sub-left">
                    <div className="sub-badges">
                      <span className="sub-priority" style={{ background: '#fee2e2', color: '#991b1b' }}>active</span>
                      <span className="sub-frequency">{sub.frequency}</span>
                      {fs && <span style={{ background: fs.bg, color: fs.color, padding: '0.2rem 0.55rem', borderRadius: '20px', fontSize: '0.72rem', fontWeight: 700 }}>{fs.label}</span>}
                    </div>
                    <div className="sub-org">{sub.orgName}</div>
                    <div className="sub-item">{sub.itemName}</div>
                    <div className="sub-impact">{sub.impactStatement}</div>
                    <NetworkAttributionBadge networkName={sub.networkName} />
                    <div style={{ fontSize: '0.82rem', color: '#555', marginTop: '0.5rem' }}>You've delivered {sub.totalDeliveries || 0} shipment{sub.totalDeliveries === 1 ? '' : 's'}</div>
                    <div style={{ fontSize: '0.82rem', color: '#555', marginTop: '0.15rem' }}>Total impact: {sub.totalImpact || 0} {sub.impactUnit || ''}</div>
                    {sub.orderStatus === 'needed' && <div style={{ fontSize: '0.8rem', color: '#92400e', marginTop: '0.2rem' }}>This month's order is awaiting processing.</div>}
                    {sub.orderStatus === 'ordered' && sub.latestOrder?.targetDeliveryDate && <div style={{ fontSize: '0.8rem', color: '#1e40af', marginTop: '0.2rem' }}>Current shipment is on the way.</div>}
                    {sub.orderStatus === 'delivered' && sub.latestOrder?.deliveredAt && <div style={{ fontSize: '0.8rem', color: '#065f46', marginTop: '0.2rem' }}>Latest shipment has been delivered.</div>}
                    {(sub.trackingNumber || sub.latestOrder?.trackingNumber) && (
                      <div style={{ fontSize: '0.8rem', color: '#1e40af', marginTop: '0.2rem' }}>
                        Tracking: {sub.carrier || sub.latestOrder?.carrier} {sub.trackingNumber || sub.latestOrder?.trackingNumber}
                        {(sub.trackingUrl || sub.latestOrder?.trackingUrl) && <>{' · '}<a href={sub.trackingUrl || sub.latestOrder?.trackingUrl} target="_blank" rel="noreferrer">Track package →</a></>}
                      </div>
                    )}
                    {milestone && <div style={{ marginTop: '0.4rem', fontSize: '0.78rem', color: '#2d6a4f', fontWeight: 600 }}>{milestone}</div>}
                  </div>
                  <div className="sub-right">
                    <div className="sub-price">{formatCurrency(sub.pricePerCycle)}<span>/{sub.frequency}</span></div>
                    <div className="sub-since">
                      {formatSubscriptionDates(sub) && <div style={{ fontSize: '0.78rem', color: '#2d6a4f', fontWeight: 500, marginTop: '0.25rem' }}>{formatSubscriptionDates(sub)}</div>}
                      Since {sub.createdAt?.toDate?.()?.toLocaleDateString() || 'recently'}
                    </div>
                    <button className="btn-cancel-sub" onClick={() => handleCancel(sub)}>End commitment</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Impact map tab */}
        {!loading && dashboardTab === 'impact' && (
          
          <ImpactMap
  activeSubscriptions={activeSubscriptions}
  donorName={
    userProfile?.firstName
      ? `${userProfile.firstName} ${
          userProfile.lastName
            ? userProfile.lastName.charAt(0).toUpperCase() + '.'
            : ''
        }`
      : 'You'
  }
/>


        )}

        {/* Upcoming bills tab */}
        {!loading && dashboardTab === 'upcoming' && (
          <div className="subscriptions-list">
            <div className="section-header"><h2>Upcoming bills</h2></div>
            {upcomingBillingGroups.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🗓️</div>
                <h2>No upcoming bills</h2>
                <p>You do not have any active subscriptions with a future billing date.</p>
              </div>
            ) : upcomingBillingGroups.map(group => (
              <div key={group.date} className="subscription-card" style={{ display: 'block' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{group.date}</div>
                  <div style={{ color: '#1e3a5f', fontWeight: 700 }}>Total due: {formatCurrency(group.total)}</div>
                </div>
                {group.items.map(item => (
                  <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '0.65rem 0', borderTop: '1px solid #f0f0f0' }}>
                    <div>
  <div style={{ fontWeight: 600 }}>{item.itemName}</div>
  <div style={{ color: '#777', fontSize: '0.88rem' }}>{item.orgName}</div>
  <NetworkAttributionBadge networkName={item.networkName} />
</div>
                    <div style={{ fontWeight: 600, color: '#1e3a5f' }}>{formatCurrency(item.pricePerCycle)}</div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Payments tab */}
        {!loading && dashboardTab === 'payments' && (
          <div className="subscriptions-list">
            <div className="section-header"><h2>Payments & payment method</h2></div>
            <div className="subscription-card" style={{ display: 'block', marginBottom: '1rem' }}>
              <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.75rem' }}>Current payment method</div>
              {currentPaymentMethod ? (
                <><div style={{ color: '#444', marginBottom: '0.75rem' }}>{currentPaymentMethod}</div><button className="btn-primary" style={{ width: 'auto' }} onClick={() => navigate(PAYMENT_METHOD_ROUTE)}>Update payment method</button></>
              ) : (
                <><div style={{ color: '#777', marginBottom: '0.75rem' }}>No payment method is currently saved.</div><button className="btn-primary" style={{ width: 'auto' }} onClick={() => navigate(PAYMENT_METHOD_ROUTE)}>Add payment method</button></>
              )}
            </div>
            {payments.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">💳</div>
                <h2>No payment history yet</h2>
                <p>Your billing history will appear here after your first successful charge.</p>
              </div>
            ) : payments.map(payment => (
              <div key={payment.id} className="subscription-card" style={{ display: 'block' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '0.85rem' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '1rem' }}>{formatDate(payment.billedAt) || 'Recent payment'}</div>
                    <div style={{ fontSize: '0.86rem', color: '#666', marginTop: '0.2rem' }}>Status: {payment.status || 'unknown'}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 700, color: '#1e3a5f', fontSize: '1rem' }}>{formatCurrency(payment.amount)}</div>
                    <div style={{ fontSize: '0.82rem', color: '#777' }}>{payment.itemsCount || payment.items?.length || 0} item{(payment.itemsCount || payment.items?.length || 0) === 1 ? '' : 's'}</div>
                  </div>
                </div>
                <div style={{ borderTop: '1px solid #f0f0f0' }}>
                  {(payment.items || []).map((item, idx) => (
                    <div key={`${payment.id}-${idx}`} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', padding: '0.7rem 0', borderBottom: idx === (payment.items || []).length - 1 ? 'none' : '1px solid #f7f7f7' }}>
                      <div>
  <div style={{ fontWeight: 600 }}>{item.itemName}</div>
  <div style={{ fontSize: '0.85rem', color: '#777' }}>{item.orgName}</div>
  <NetworkAttributionBadge networkName={item.networkName} />
  {item.frequency && (
    <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.15rem' }}>
      {item.frequency}
    </div>
  )}
</div>
                      <div style={{ fontWeight: 600, color: '#1e3a5f', whiteSpace: 'nowrap' }}>{formatCurrency(item.pricePerCycle)}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default DonorDashboard;